import React from "react";
import { Link } from "react-router-dom";
import { Lock, Users, Crown, Coins, Globe, BadgeCheck, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Tilgang-velger – samler tidligere "synlighet" og "krever betalt nivå"
 * til én konsistent radio + tillegg-checkboxer.
 *
 * Kombinasjoner går KUN oppover (utvider tilgang aldri innover):
 *   privat   → ingen tillegg
 *   venner   → kan også gi VIP gratis
 *   vip      → kan også gi venner gratis
 *   paid     → pris i tokens, kan gi VIP/venner gratis unntak
 *   alle     → ingen tillegg
 *
 * Props:
 *   value = { mode: "private"|"friends"|"vip"|"paid"|"public", price?: number, vipFree?: bool, friendsFree?: bool }
 *   onChange(value)
 */
export default function AccessModeSelector({ value, onChange }) {
  const { user } = useAuth();
  const isSellerVerified = !!user?.seller_verified;
  const v = value || { mode: "private" };
  const set = (patch) => onChange({ ...v, ...patch });

  const options = [
    { key: "private", label: "Privat", desc: "Kun jeg ser det", icon: Lock },
    { key: "friends", label: "Venner", desc: "Mine bekreftede venner", icon: Users },
    { key: "vip",     label: "VIP-medlemmer", desc: "Alle med aktivt VIP-medlemskap", icon: Crown },
    { key: "paid",    label: "Mot betaling", desc: "Vises uskarpt – kjøpes med tokens", icon: Coins },
    { key: "public",  label: "Alle", desc: "Synlig for hele plattformen", icon: Globe },
  ];

  return (
    <div className="space-y-4" data-testid="access-mode-selector">
      <div>
        <div className="font-meta mb-2">Hvem skal se dette?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map((o) => {
            const Icon = o.icon;
            const active = v.mode === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => set({
                  mode: o.key,
                  // Reset tillegg når mode skifter
                  vipFree: false,
                  friendsFree: false,
                  price: o.key === "paid" ? (v.price || 50) : 0,
                })}
                className={`flex items-start gap-3 p-3 text-left border transition ${
                  active
                    ? "border-[var(--magenta)] bg-[var(--magenta)]/8 text-[var(--cream)]"
                    : "border-white/10 hover:border-white/30 text-[var(--cream-soft)]"
                }`}
                data-testid={`access-mode-${o.key}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${active ? "text-[var(--magenta)]" : ""}`} />
                <div>
                  <div className="font-fraunces italic text-base leading-tight">{o.label}</div>
                  <div className="font-meta text-[10px] mt-0.5">{o.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* TILLEGG som dukker opp basert på valg */}
      {v.mode === "friends" && (
        <Toggle
          label="Også VIP-medlemmer får se"
          checked={!!v.vipFree}
          onChange={(c) => set({ vipFree: c })}
          testId="access-vip-free-toggle"
        />
      )}
      {v.mode === "vip" && (
        <Toggle
          label="Også mine venner får se"
          checked={!!v.friendsFree}
          onChange={(c) => set({ friendsFree: c })}
          testId="access-friends-free-toggle"
        />
      )}
      {v.mode === "paid" && (
        <div className="space-y-3 border-l-2 border-[var(--magenta)]/40 pl-4 py-2">
          <div>
            <div className="font-meta mb-2">Pris i tokens (10–500)</div>
            <input
              type="number"
              min={10}
              max={500}
              value={v.price || 50}
              onChange={(e) => set({ price: Math.max(10, Math.min(500, parseInt(e.target.value) || 0)) })}
              className="input-editorial w-32"
              data-testid="access-price-input"
            />
            <div className="font-meta mt-1.5 text-[10px] text-[var(--cream-faint)]">
              Du tjener {Math.floor((v.price || 50) * 0.9)} tokens per kjøp · 10% til plattform
            </div>
          </div>
          <Toggle
            label="VIP-medlemmer får se"
            checked={!!v.vipFree}
            onChange={(c) => set({ vipFree: c })}
            testId="access-paid-vip-free"
          />
          <Toggle
            label="Mine venner får se"
            checked={!!v.friendsFree}
            onChange={(c) => set({ friendsFree: c })}
            testId="access-paid-friends-free"
          />
        </div>
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group" data-testid={testId}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[var(--magenta)]"
      />
      <span className="font-manrope text-sm text-[var(--cream-soft)] group-hover:text-[var(--cream)] transition-colors">
        {label}
      </span>
    </label>
  );
}

/**
 * Pakk verdi-objektet til API-format som backend forstår.
 *
 * Backend forventer (eksisterende struktur):
 *   visibility: "private" | "friends" | "vip" | "public"
 *   price_tokens: int (0 hvis gratis)
 *   access_friends_free: bool   (NY)
 *   access_vip_free: bool       (NY)
 */
export function packAccess(v) {
  if (!v) return { visibility: "private", price_tokens: 0 };
  switch (v.mode) {
    case "private":
      return { visibility: "private", price_tokens: 0,
               access_friends_free: false, access_vip_free: false };
    case "friends":
      return { visibility: "friends", price_tokens: 0,
               access_friends_free: true, access_vip_free: !!v.vipFree };
    case "vip":
      return { visibility: "vip", price_tokens: 0,
               access_friends_free: !!v.friendsFree, access_vip_free: true };
    case "paid":
      return { visibility: "public", price_tokens: v.price || 50,
               access_friends_free: !!v.friendsFree, access_vip_free: !!v.vipFree };
    case "public":
    default:
      return { visibility: "public", price_tokens: 0,
               access_friends_free: false, access_vip_free: false };
  }
}

export function unpackAccess(item) {
  if (!item) return { mode: "private" };
  const price = item.price_tokens || 0;
  const vis = item.visibility || "public";
  if (vis === "private") return { mode: "private" };
  if (price > 0) return {
    mode: "paid", price,
    vipFree: !!item.access_vip_free,
    friendsFree: !!item.access_friends_free,
  };
  if (vis === "friends") return {
    mode: "friends",
    vipFree: !!item.access_vip_free,
  };
  if (vis === "vip") return {
    mode: "vip",
    friendsFree: !!item.access_friends_free,
  };
  return { mode: "public" };
}
